import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { loginSchema } from '@hirebridge/validation'
import { ApiClientError } from '@hirebridge/api-client'
import { useAuth } from '../lib/auth'
import { GoogleSignInButton } from './GoogleSignInButton'

export function LoginModal() {
  const { isLoginModalOpen, closeLoginModal, login, modalRedirectPath } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLoginModalOpen) {
        closeLoginModal()
      }
    }
    if (isLoginModalOpen) {
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isLoginModalOpen, closeLoginModal])

  if (!isLoginModalOpen) return null

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    const parsed = loginSchema.safeParse({ email, password })

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please check your login details.')
      return
    }

    setBusy(true)

    try {
      const loggedInUser = await login(email, password)
      closeLoginModal()
      if (modalRedirectPath) {
        navigate(modalRedirectPath)
      } else if (loggedInUser.companies && loggedInUser.companies.length > 0) {
        navigate('/employer')
      } else if (loggedInUser.roles && (loggedInUser.roles.includes('employer') || loggedInUser.roles.includes('owner') || loggedInUser.roles.includes('admin') || loggedInUser.roles.includes('recruiter'))) {
        navigate('/employer')
      } else {
        navigate('/candidate')
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Invalid credentials. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleRegisterClick = () => {
    closeLoginModal()
  }

  return (
    <div
      className="auth-modal-overlay"
      onClick={closeLoginModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      <div
        className="auth-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal-close-btn"
          onClick={closeLoginModal}
          aria-label="Close modal"
        >
          ✕
        </button>

        <div className="auth-header">
          <div className="brand-icon auth-modal-brand-icon" style={{ padding: '4px', background: 'transparent' }}>
            <img src="/logo-icon.png" alt="FibonacciPi" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <h2 id="login-modal-title">Welcome Back</h2>
          <p>Sign in to manage your jobs, applications, and team</p>
        </div>

        {error && (
          <div className="alert-box alert-error auth-alert" role="alert">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Google OAuth 1-Click Sign In */}
        <GoogleSignInButton
          mode="signin"
          onSuccess={() => {
            closeLoginModal()
            if (modalRedirectPath) {
              navigate(modalRedirectPath)
            }
          }}
          onError={(err) => setError(err)}
        />

        <div className="auth-divider">
          <span>or continue with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="modal-email">Email or Phone</label>
            <div className="input-with-icon">
              <span className="input-icon">✉️</span>
              <input
                id="modal-email"
                type="text"
                autoComplete="username"
                placeholder="e.g. asha@acme.test or ravi@example.test"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
              <label htmlFor="modal-password" style={{ margin: 0 }}>Password</label>
              <Link
                to="/forgot-password"
                onClick={closeLoginModal}
                className="auth-switch-link"
                style={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                Forgot password?
              </Link>
            </div>
            <div className="input-with-icon">
              <span className="input-icon">🔒</span>
              <input
                id="modal-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-auth-submit"
            disabled={busy}
          >
            {busy ? (
              <span className="btn-busy-content">
                <span className="spinner-inline"></span> Signing in…
              </span>
            ) : (
              'Sign In to Account'
            )}
          </button>
        </form>

        <div className="auth-modal-footer" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', textAlign: 'center' }}>
          <div>
            Don't have an account?{' '}
            <Link to="/register" onClick={handleRegisterClick} className="auth-switch-link" style={{ fontWeight: 700 }}>
              Create free account
            </Link>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', fontSize: '0.8125rem', color: '#64748b' }}>
            <Link to="/register?role=candidate" onClick={handleRegisterClick} style={{ color: '#2563eb' }}>
              🎯 Candidate
            </Link>
            <span>•</span>
            <Link to="/register?role=employer" onClick={handleRegisterClick} style={{ color: '#2563eb' }}>
              💼 Employer
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
